import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import React, { useState } from "react";

const ChangePassword = () => {
  const user = useSelector((state) => state?.user);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emptyFields, setEmptyFields] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // States to toggle visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const validatePassword = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple form submissions
    if (isSubmitting) return;

    setIsSubmitting(true); // Disable button on submit
    setEmptyFields([]); // Reset empty fields

    // Check for empty fields
    const fields = [
      { name: "currentPassword", value: currentPassword },
      { name: "newPassword", value: newPassword },
      { name: "confirmPassword", value: confirmPassword },
    ];

    const emptyFieldsList = fields
      .filter((field) => field.value === "")
      .map((field) => field.name);
    if (emptyFieldsList.length > 0) {
      setEmptyFields(emptyFieldsList);
      toast.error("Please complete all required fields.");
      setIsSubmitting(false);
      return;
    }

    // Validate new password strength
    if (!validatePassword(newPassword)) {
      toast.error("New password is invalid.");
      setIsSubmitting(false);
      return;
    }

    // Validate that new password matches confirm password
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    const adminId = user?.user?.businessId;
    // Prepare the data to be sent
    const processedData = {
      currentPassword,
      newPassword,
      adminId
    };

    const token = user.user?.token;

    try {
      // Call the API to validate current password and change the password with authentication header
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/change-password`,
        processedData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Send the token in the Authorization header
          },
        }
      );

      if (response.data.success) {
        toast.success("Password changed successfully.");
        setTimeout(() => {
          navigate("/login"); // Navigate to login after successful password change
        }, 3000);
      } else {
        // Handle specific failure reasons returned by the API
        const errorMsg = response.data.message || "Failed to change password.";
        toast.error(errorMsg);
        setIsSubmitting(false); // Re-enable the button if password change fails
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message;

      if (errorMsg === "Invalid token") {
        // Handle token expiration or invalid token error
        toast.error("Session expired. Please log in again.");
        navigate("/login"); // Redirect to login page
      } else {
        toast.error(errorMsg || "An error occurred. Please try again.");
        setIsSubmitting(false); // Re-enable the button if error occurs
      }
    }
  };

  return (
    <div style={styles.container}>
      <ToastContainer />
      <div style={styles.header}>
        <h2 style={styles.title}>Change Password</h2>
      </div>
      <div style={styles.formContainer}>
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Current Password</label>
            <div style={styles.passwordContainer}>
              <input
                type={showCurrentPassword ? "text" : "password"}
                style={{
                  ...styles.input,
                  borderColor: emptyFields.includes("currentPassword")
                    ? "red"
                    : "none",
                }}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <span
                style={styles.icon}
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>New Password</label>
            <div style={styles.passwordContainer}>
              <input
                type={showNewPassword ? "text" : "password"}
                style={{
                  ...styles.input,
                  borderColor: emptyFields.includes("newPassword")
                    ? "red"
                    : "none",
                }}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <span
                style={styles.icon}
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Confirm Password</label>
            <div style={styles.passwordContainer}>
              <input
                type={showNewPassword ? "text" : "password"}
                style={{
                  ...styles.input,
                  borderColor: emptyFields.includes("confirmPassword")
                    ? "red"
                    : "none",
                }}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <span
                style={styles.icon}
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
          <button
            type="submit"
            style={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Change Password" : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: "400px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    backgroundColor: "#fff",
    fontFamily: "Poppins",
    fontWeight: 600,
  },
  header: {
    backgroundColor: "#064260",
    padding: "17px 20px",
    color: "#fff",
    textAlign: "left",
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 600,
  },
  formContainer: {
    padding: "20px",
  },
  formGroup: {
    marginBottom: "15px",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontFamily: "Poppins",
    fontWeight: 600,
    marginBottom: "5px",
  },
  input: {
    width: "100%",
    padding: "7px",
    fontSize: "14px",
    borderRadius: "5px",
    border: "none",
    backgroundColor: "#f9f9f9",
    outline: "none",
  },
  submitButton: {
    padding: "8px",
    fontSize: "12px",
    fontWeight: 600,
    backgroundColor: "#27B99A",
    color: "#fff",
    border: "none",
    borderRadius: "25px",
    cursor: "pointer",
    width: "45%",
  },
  error: {
    color: "red",
    fontSize: "12px",
    marginBottom: "10px",
  },
  success: {
    color: "green",
    fontSize: "12px",
    marginBottom: "10px",
  },
  passwordContainer: {
    position: "relative",
  },
  icon: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
    color: "#888",
  },
};

export default ChangePassword;
